import { useQuery, useQueryClient } from "@tanstack/react-query"
import {
  BellOffIcon,
  BellRingIcon,
  ChevronDownIcon,
  CircleSlashIcon,
  KeyboardIcon,
  LogOutIcon,
  RefreshCwIcon,
  SettingsIcon,
  SmileIcon,
  SunMoonIcon,
} from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"
import { toast } from "sonner"
import type { Attention } from "@beacon/core/chat"
import {
  attentionKey,
  attentionQuery,
  describeDoNotDisturb,
  pauseChoices,
  pausedUntilOf,
  setAttention,
} from "@/chat/attention.ts"
import { useStatusShown } from "@/chat/presence-marks.tsx"
import {
  availabilityQuery,
  clearStatus,
  describePresence,
  describeStatus,
  presenceKeys,
  setAppearOffline,
  touchesMember,
} from "@/chat/presence.ts"
import { StatusDialog } from "@/chat/status-dialog.tsx"
import {
  Avatar,
  colourSchemes,
  Disc,
  Kbd,
  RoleGate,
  useColourScheme,
  useViewerZone,
  WithPresence,
} from "@/design-system/index.ts"
import { installIcon, useInstallOffer } from "@/installed-app/install-offer.tsx"
import { reloadToUpdate, useUpdate } from "@/installed-app/updates.ts"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/primitives/dropdown-menu.tsx"
import { SidebarMenuButton } from "@/primitives/sidebar.tsx"
import type { Me } from "@/sign-in/client.ts"
import { describeStanding } from "./describe.ts"
import { adminDestinations, destinations } from "./destinations.ts"

/*
 * Everything about the signed-in Member and their own browser, behind the sidebar's workspace row:
 * who they are, the pages they set up once rather than work in (My Connectors, Connect an agent),
 * the colour scheme, the keyboard shortcuts, installing Beacon where the browser can, the way into
 * Administration for an Admin, and signing out. While a new release of Beacon waits, "Update
 * Beacon" leads it. Before the shell had it, signing out was a button on Home and the colour scheme
 * lived only on the kitchen sink. Their Chat availability is here too: their status (set, edit or
 * clear it), Appear offline, and a dot on their disc saying how colleagues see them now. So is
 * pausing Chat's notifications (Do not disturb), resuming them, and the way to their settings.
 */

/**
 * The Member's own row (their photo or initials, and their name) and the menu it opens. Collapsed to
 * icons, the row is their disc alone.
 */
export function AccountMenu({
  me,
  onShowShortcuts,
  onSignOut,
}: {
  me: Me
  onShowShortcuts: () => void
  onSignOut: () => void
}) {
  const { scheme, chooseScheme } = useColourScheme()
  const update = useUpdate().state.status !== "current"
  const { offered, install } = useInstallOffer()
  const InstallIcon = installIcon(false)
  const queryClient = useQueryClient()
  const availability = useQuery(availabilityQuery).data
  const status = useStatusShown(availability?.status)
  const [settingStatus, setSettingStatus] = useState(false)
  const attention = useQuery(attentionQuery).data
  const zone = useViewerZone()
  const dnd = attention && describeDoNotDisturb(attention, Date.now(), zone)
  const paused = Boolean(attention?.pausedUntil && Date.parse(attention.pausedUntil) > Date.now())

  async function pause(pausedUntil: string | null) {
    try {
      queryClient.setQueryData<Attention>(attentionKey, await setAttention({ pausedUntil }))
      toast.success(pausedUntil ? "Notifications paused" : "Notifications resumed")
    } catch {
      toast.error("That didn't save. Try again.")
    }
  }

  async function changeAvailability(change: () => ReturnType<typeof clearStatus>, done: string) {
    try {
      queryClient.setQueryData(presenceKeys.availability, await change())
      void queryClient.invalidateQueries({
        predicate: ({ queryKey }) => touchesMember(queryKey, me.id),
      })
      toast.success(done)
    } catch {
      toast.error("That didn't save. Try again.")
    }
  }

  const disc = availability ? (
    <WithPresence presence={availability.presence} size={20} on="sidebar">
      <Disc person={me} size={20} />
    </WithPresence>
  ) : (
    <Disc person={me} size={20} />
  )
  const seenAs =
    availability &&
    describePresence(availability.presence, { appearOffline: availability.appearOffline })

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <SidebarMenuButton
              size="lg"
              aria-label={`${me.name}, ${seenAs ? `${seenAs.toLowerCase()}, ` : ""}account menu`}
              // The chevron is 14px, not the row's 16px icons.
              className="w-auto min-w-0 flex-1 gap-2 text-foreground [&_svg]:size-3.5"
            />
          }
        >
          {disc}
          <span className="truncate text-ui font-semibold group-data-[collapsible=icon]:hidden">
            {me.name}
          </span>
          <ChevronDownIcon className="size-3.5 shrink-0 text-subtle-foreground group-data-[collapsible=icon]:hidden" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-64">
          <div className="flex gap-2.5 px-2 py-2">
            <Avatar person={me} size={32} />
            <div className="min-w-0">
              <p className="truncate text-ui font-medium">{me.name}</p>
              <p className="truncate text-xs text-muted-foreground">{describeStanding(me)}</p>
              {seenAs && <p className="truncate text-xs text-muted-foreground">{seenAs}</p>}
              {dnd && <p className="truncate text-xs text-muted-foreground">{dnd}</p>}
            </div>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => setSettingStatus(true)}>
              {status ? (
                <span aria-hidden className="inline-grid size-4 shrink-0 place-items-center">
                  {status.emoji ?? "💬"}
                </span>
              ) : (
                <SmileIcon />
              )}
              <span className="truncate">
                {status ? (
                  <>
                    <span className="sr-only">Edit your status: </span>
                    {status.text ?? describeStatus(status)}
                  </>
                ) : (
                  "Set a status…"
                )}
              </span>
            </DropdownMenuItem>
            {status && (
              <DropdownMenuItem
                onClick={() => void changeAvailability(clearStatus, "Status cleared")}
              >
                <CircleSlashIcon />
                Clear status
              </DropdownMenuItem>
            )}
            <DropdownMenuCheckboxItem
              checked={availability?.appearOffline ?? false}
              disabled={!availability}
              onCheckedChange={(checked) =>
                void changeAvailability(
                  () => setAppearOffline(checked),
                  checked ? "You appear offline" : "You appear online again",
                )
              }
            >
              Appear offline
            </DropdownMenuCheckboxItem>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger disabled={!attention}>
                <BellOffIcon />
                {paused ? "Notifications paused" : "Pause notifications"}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                {paused && (
                  <>
                    <DropdownMenuItem onClick={() => void pause(null)}>
                      <BellRingIcon />
                      Resume notifications
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                {pauseChoices.map(({ value, label }) => (
                  <DropdownMenuItem
                    key={value}
                    onClick={() => void pause(pausedUntilOf(value, Date.now()))}
                  >
                    {label}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem render={<Link to="/chat/notifications" />}>
                  <SettingsIcon />
                  Notification settings
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {update && (
            <>
              <DropdownMenuItem onClick={reloadToUpdate}>
                <RefreshCwIcon />
                Update Beacon
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuGroup>
            {destinations
              .filter((destination) => destination.accountMenu)
              .map(({ to, title, Icon, chord }) => (
                <DropdownMenuItem key={to} render={<Link to={to} />}>
                  <Icon />
                  {title}
                  <span className="ml-auto">
                    <Kbd shortcut={chord} />
                  </span>
                </DropdownMenuItem>
              ))}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <SunMoonIcon />
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup value={scheme} onValueChange={chooseScheme}>
                  {colourSchemes.map(({ value, label, Icon }) => (
                    <DropdownMenuRadioItem key={value} value={value}>
                      <Icon />
                      {label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuItem onClick={onShowShortcuts}>
              <KeyboardIcon />
              Keyboard shortcuts
              <span className="ml-auto">
                <Kbd shortcut="shortcuts" />
              </span>
            </DropdownMenuItem>
            {offered && (
              <DropdownMenuItem onClick={install}>
                <InstallIcon />
                Install Beacon
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          {/* Administration opens on its first page, with the sidebar turned to the Admin's pages. */}
          <RoleGate role={me.role} admits="admin">
            <DropdownMenuItem render={<Link to={adminDestinations[0]?.to ?? "/"} />}>
              <SettingsIcon />
              Administration
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </RoleGate>
          <DropdownMenuItem onClick={onSignOut}>
            <LogOutIcon />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <StatusDialog
        open={settingStatus}
        onOpenChange={setSettingStatus}
        me={me.id}
        current={availability?.status ?? null}
      />
    </>
  )
}
